/* eslint-disable @typescript-eslint/no-explicit-any */
import { List, ListProps, ListSubheader } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { FC } from "react";
import { matchPath } from "react-router-dom";
import useEntitlements from "../hooks/useEntitlements";
import { useSelector } from "../store";
import { TNavItem } from "../types/navItem";
import { shouldRenderMenuItem } from "../utils/shouldRenderMenuItem";
import NavItem from "./NavItem";

interface NavSectionProps extends ListProps {
  items: TNavItem[];
  pathname: string;
  title: string;
  ldOrgFlag?: string;
  allowedRoles?: string[];
  collapsed?: boolean;
  ldFlag?: string;
  tier?: string | string[];
  capability?: string;
}

const renderNavItems = ({
  depth = 0,
  items,
  pathname,
  launchDarklyFlags,
  adminSettings,
  orgId,
  userRoles,
  collapsed,
  can,
  authId,
  entitlementTier,
}: {
  items: TNavItem[];
  pathname: string;
  depth?: number;
  launchDarklyFlags: any;
  adminSettings: any;
  orgId: string;
  userRoles: string[];
  collapsed?: boolean;
  can: (action: string) => boolean;
  authId?: string;
  entitlementTier?: string;
}): JSX.Element => (
  <List disablePadding>
    {items.reduce(
      (acc, item) =>
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        reduceChildRoutes({
          acc,
          item,
          pathname,
          depth,
          launchDarklyFlags,
          adminSettings,
          orgId,
          userRoles,
          collapsed,
          can,
          authId,
          entitlementTier,
        }),
      [],
    )}
  </List>
);

const reduceChildRoutes = ({
  acc,
  pathname,
  item,
  depth,
  launchDarklyFlags,
  adminSettings,
  orgId,
  userRoles,
  collapsed,
  can,
  authId,
  entitlementTier,
}: {
  acc: JSX.Element[];
  pathname: string;
  item: TNavItem;
  depth: number;
  launchDarklyFlags: any;
  adminSettings: any;
  orgId: string;
  userRoles: string[];
  collapsed?: boolean;
  can: (action: string) => boolean;
  authId?: string;
  entitlementTier?: string;
}): Array<JSX.Element> => {
  const key = `${item.title}-${depth}`;
  const exactMatch = item.path
    ? !!matchPath(
        {
          path: item.path,
          end: true,
        },
        pathname,
      )
    : false;

  if (item.children) {
    const partialMatch = item.path
      ? !!matchPath(
          {
            path: item.path,
            end: false,
          },
          pathname,
        )
      : false;

    // Check if any children should be rendered
    const childrenToRender = item.children.filter((child) =>
      shouldRenderMenuItem(
        child,
        launchDarklyFlags,
        orgId,
        userRoles,
        can,
        authId,
        entitlementTier,
        adminSettings,
      ),
    );

    if (childrenToRender.length > 0) {
      acc.push(
        <NavItem
          active={partialMatch}
          depth={depth}
          icon={item.icon}
          info={item.info}
          key={key}
          open={partialMatch}
          path={item.path}
          title={item.title}
          collapsed={collapsed}
        >
          {renderNavItems({
            depth: depth + 1,
            items: childrenToRender,
            pathname,
            launchDarklyFlags,
            adminSettings,
            orgId,
            userRoles,
            collapsed,
            can,
            authId,
            entitlementTier,
          })}
        </NavItem>,
      );
    }
  } else if (
    shouldRenderMenuItem(
      item,
      launchDarklyFlags,
      orgId,
      userRoles,
      can,
      authId,
      entitlementTier,
      adminSettings,
    )
  ) {
    acc.push(
      <NavItem
        active={exactMatch}
        depth={depth}
        icon={item.icon}
        info={item.info}
        path={item.path}
        title={item.title}
        collapsed={collapsed}
        key={key}
      />,
    );
  }

  return acc;
};

const NavSection: FC<NavSectionProps> = (props) => {
  const {
    items,
    pathname,
    title,
    ldOrgFlag,
    allowedRoles,
    collapsed,
    ldFlag,
    tier,
    capability,
    ...other
  } = props;
  const flags = useFlags();
  const currentOrg = useSelector((state) => state.user.currentOrganization);
  const userRoles = useSelector((state) => state.user.roles);
  const { can, entitlements } = useEntitlements();

  return (
    <List
      subheader={
        <ListSubheader
          disableGutters
          disableSticky
          sx={{
            display: "block",
            height: 28,
            pl: collapsed ? 0 : 2,
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? "translateX(-8px)" : "translateX(0)",
            overflow: "hidden",
            transition: "opacity 0.25s ease 0.25s, transform 0.25s ease 0.25s",
            color: "text.disabled",
            fontSize: "0.75rem",
            lineHeight: 2.5,
            fontWeight: 500,
            letterSpacing: 0.5,
          }}
        >
          {title}
        </ListSubheader>
      }
      {...other}
    >
      {renderNavItems({
        items,
        pathname,
        launchDarklyFlags: flags,
        adminSettings: currentOrg?.adminSettings,
        orgId: currentOrg?.id,
        userRoles,
        collapsed,
        can,
        authId: currentOrg?.authId,
        entitlementTier: entitlements?.tier,
      })}
    </List>
  );
};

export default NavSection;
